import React, { useState, useEffect } from 'react';
import { Typography, CircularProgress, Alert, Tooltip, Box, TextField, Button, Rating } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { TrialState } from '../../types/trial';
import {
  OverlayContainer,
  ContentWrapper,
  IconContainer,
  ContactSalesLink,
  MinimizeButton,
} from './TrialOverlay.styles';
import { SALES_EMAIL } from '../../constants/endpoints';
import { useUsageStats } from '../../context/UsageStatsContext';
import { submitTrialFeedback } from '../../services/Trial/trialApi';

interface TrialOverlayProps {
  trialState: TrialState;
  onCollapse?: () => void;
  organizationId?: string;
}

export const TrialOverlay: React.FC<TrialOverlayProps> = ({
  trialState,
  onCollapse,
  organizationId,
}) => {
  const { t } = useTranslation();
  const { usageStats, fetchUsageStats } = useUsageStats();
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  // Check localStorage to see if feedback was already submitted for this organization
  const feedbackStorageKey = organizationId ? `trialFeedbackSubmitted_${organizationId}` : null;
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(() => {
    if (!feedbackStorageKey) return false;
    return localStorage.getItem(feedbackStorageKey) === 'true';
  });

  // Sync feedbackSubmitted state when organizationId changes
  useEffect(() => {
    if (feedbackStorageKey) {
      setFeedbackSubmitted(localStorage.getItem(feedbackStorageKey) === 'true');
    } else {
      setFeedbackSubmitted(false);
    }
  }, [feedbackStorageKey]);

  // Fetch usage stats for expired trials
  useEffect(() => {
    if (trialState === 'expired' && organizationId) {
      fetchUsageStats(organizationId);
    }
  }, [trialState, organizationId, fetchUsageStats]);

  const stats = organizationId ? usageStats[organizationId] : null;

  // Don't render if user has access, state is loading, or eligible (auto-activation in progress)
  if (trialState === 'active' || trialState === 'loading' || trialState === 'eligible') {
    return null;
  }

  const handleFeedbackSubmit = async () => {
    if (!feedbackRating || !organizationId) return;

    setFeedbackSubmitting(true);
    setFeedbackError(null);

    try {
      await submitTrialFeedback({
        organization_id: parseInt(organizationId),
        rating: feedbackRating,
        comment: feedbackComment || undefined,
        conversation_count: stats?.conversation_count,
        estimated_time_saved_hours: stats?.estimated_time_saved_hours,
      });
      setFeedbackSubmitted(true);
      if (feedbackStorageKey) {
        localStorage.setItem(feedbackStorageKey, 'true');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setFeedbackError(t('trialFeedbackError'));
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const formatTimeSaved = () => {
    if (!stats) return '';
    const hours = stats.estimated_time_saved_hours;
    if (hours >= 1) {
      return t('usageStatsHours', { hours, count: hours });
    }
    return t('usageStatsLessThanHour');
  };

  const isExpired = trialState === 'expired';

  return (
    <OverlayContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Minimize button */}
      {onCollapse && (
        <Tooltip title={t('collapseChat')} placement="left">
          <MinimizeButton onClick={onCollapse} aria-label={t('collapseChat')}>
            <ExpandMoreIcon />
          </MinimizeButton>
        </Tooltip>
      )}
      <ContentWrapper>
        {isExpired && (
          <>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              mb: 1.5
            }}>
              <TimerOffIcon sx={{ fontSize: 28, color: '#f44336' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#424242',
                }}
              >
                {t('trialExpiredTitle')}
              </Typography>
            </Box>

            {/* Usage Stats Summary - Compact */}
            {stats && stats.conversation_count > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  mb: 1.5,
                  py: 1,
                  px: 1.5,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, color: '#757575' }} />
                  <Typography sx={{ fontWeight: 600, color: '#424242', fontSize: '0.875rem' }}>
                    {formatTimeSaved()}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#757575' }}>
                    {t('usageStatsTimeSaved')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: '#757575' }} />
                  <Typography sx={{ fontWeight: 600, color: '#424242', fontSize: '0.875rem' }}>
                    {stats.conversation_count}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#757575' }}>
                    {t('usageStatsConversations', { count: stats.conversation_count })}
                  </Typography>
                </Box>
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                color: '#616161',
                lineHeight: 1.5,
                mb: 1.5,
                fontSize: '0.85rem',
              }}
            >
              {t('trialExpiredDescription')}
            </Typography>

            {/* Feedback Form - Compact */}
            {!feedbackSubmitted ? (
              <Box
                sx={{
                  width: '100%',
                  p: 1.5,
                  borderRadius: '10px',
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  mb: 1.5,
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: '#424242',
                    mb: 1,
                    fontSize: '0.85rem',
                  }}
                >
                  {t('trialFeedbackTitle')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <Rating
                    value={feedbackRating}
                    onChange={(_, newValue) => setFeedbackRating(newValue)}
                    size="medium"
                    sx={{
                      '& .MuiRating-iconFilled': { color: '#1a237e' },
                      '& .MuiRating-iconHover': { color: '#3949ab' },
                    }}
                  />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={t('trialFeedbackPlaceholder')}
                  value={feedbackComment}
                  onChange={(e) => setFeedbackComment(e.target.value)}
                  size="small"
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.8rem',
                    },
                  }}
                />
                {feedbackError && (
                  <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: '0.75rem' }}>
                    {feedbackError}
                  </Alert>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleFeedbackSubmit}
                  disabled={!feedbackRating || feedbackSubmitting}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '6px',
                    py: 0.75,
                    fontSize: '0.85rem',
                    background: '#1a237e',
                    '&:hover': { background: '#0d1657' },
                  }}
                >
                  {feedbackSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    t('trialFeedbackSubmit')
                  )}
                </Button>
              </Box>
            ) : (
              <Alert
                severity="success"
                sx={{ mb: 1.5, width: '100%', py: 0.5, fontSize: '0.85rem', justifyContent: 'center' }}
              >
                {t('trialFeedbackThankYou')}
              </Alert>
            )}

            <ContactSalesLink
              href={`mailto:${SALES_EMAIL}?subject=AppManager AI License Inquiry`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginTop: '8px', padding: '10px 24px', fontSize: '0.9rem' }}
            >
              {t('trialContactSales')}
            </ContactSalesLink>
          </>
        )}

        {trialState === 'not_eligible' && (
          <>
            <IconContainer variant="expired">
              <TimerOffIcon sx={{ fontSize: 40, color: '#9e9e9e' }} />
            </IconContainer>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#424242',
                marginBottom: '12px',
              }}
            >
              {t('trialNotAvailable')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#616161',
                lineHeight: 1.6,
              }}
            >
              {t('trialNotAvailableDescription')}
            </Typography>
            <ContactSalesLink
              href={`mailto:${SALES_EMAIL}?subject=AppManager AI License Inquiry`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('trialContactSales')}
            </ContactSalesLink>
          </>
        )}
      </ContentWrapper>
    </OverlayContainer>
  );
};
